import { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { ProgressSpinner } from 'primereact/progressspinner';

import ReportingService from '../../services/reportingService';
import {
  ApiTransactionDetail,
  VoxOrderDetail,
  AuwOrderDetail,
  DashboardDetailRequest,
  PagedResult,
} from '../../models/reporting/reporting';

export type DetailType = 'credit' | 'fees' | 'vox' | 'auw';

interface DashboardDetailDialogProps {
  visible: boolean;
  onHide: () => void;
  type: DetailType;
  startDate: Date;
  endDate: Date;
  vendor?: string;
  status?: string;
}

const PAGE_SIZE = 25;

const DashboardDetailDialog = (props: DashboardDetailDialogProps): JSX.Element => {
  const { visible, onHide, type, startDate, endDate, vendor, status } = props;
  const reportingService = ReportingService.getInstance();

  const [loading, setLoading] = useState(false);
  const [apiData, setApiData] = useState<PagedResult<ApiTransactionDetail> | null>(null);
  const [voxData, setVoxData] = useState<PagedResult<VoxOrderDetail> | null>(null);
  const [auwData, setAuwData] = useState<PagedResult<AuwOrderDetail> | null>(null);
  const [page, setPage] = useState(1);
  const [first, setFirst] = useState(0);

  useEffect(() => {
    if (visible) {
      setPage(1);
      setFirst(0);
      fetchData(1);
    }
  }, [visible, type, vendor, status]);

  const fetchData = (pageNum: number) => {
    setLoading(true);
    const request: DashboardDetailRequest = {
      startDate,
      endDate,
      vendor,
      status,
      page: pageNum,
      pageSize: PAGE_SIZE,
    };

    if (type === 'vox') {
      reportingService
        .getVoxDetails(request)
        .then((result) => {
          setVoxData(result);
          setApiData(null);
          setAuwData(null);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else if (type === 'auw') {
      reportingService
        .getAuwDetails(request)
        .then((result) => {
          setAuwData(result);
          setApiData(null);
          setVoxData(null);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      const fetcher = type === 'credit' ? reportingService.getCreditDetails : reportingService.getFeesDetails;
      fetcher
        .call(reportingService, request)
        .then((result) => {
          setApiData(result);
          setVoxData(null);
          setAuwData(null);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  };

  const onPageChange = (e: PaginatorPageChangeEvent) => {
    const newPage = e.page + 1;
    setPage(newPage);
    setFirst(e.first);
    fetchData(newPage);
  };

  const getTitle = () => {
    const labels: Record<DetailType, string> = {
      credit: 'Credit Request Details',
      fees: 'Fees Request Details',
      vox: 'Vox Order Details',
      auw: 'AUW Order Details',
    };
    let title = labels[type];
    if (vendor) title += ` — ${vendor}`;
    if (status) title += ` (${status})`;
    return title;
  };

  const successBodyTemplate = (rowData: ApiTransactionDetail) => {
    return (
      <Tag
        severity={rowData.success ? 'success' : 'danger'}
        value={rowData.success ? 'Success' : 'Failed'}
        className="text-xs"
      />
    );
  };

  const durationBodyTemplate = (rowData: ApiTransactionDetail) => {
    return <span>{rowData.durationMs.toLocaleString()} ms</span>;
  };

  const ruleExecutionBodyTemplate = (rowData: ApiTransactionDetail) => {
    return rowData.ruleExecutionMs != null ? <span>{rowData.ruleExecutionMs.toLocaleString()} ms</span> : <span className="text-gray-400">—</span>;
  };

  const serviceProviderBodyTemplate = (rowData: ApiTransactionDetail) => {
    return rowData.serviceProviderMs != null ? <span>{rowData.serviceProviderMs.toLocaleString()} ms</span> : <span className="text-gray-400">—</span>;
  };

  const dateBodyTemplate = (rowData: ApiTransactionDetail | VoxOrderDetail) => {
    const date = 'requestTime' in rowData ? rowData.requestTime : rowData.createdDate;
    return <span>{new Date(date).toLocaleString()}</span>;
  };

  const orderStatusSeverityMap: Record<string, 'success' | 'danger' | 'warning' | 'info'> = {
    Success: 'success',
    Failed: 'danger',
    Pending: 'info',
    AwaitingCallback: 'info',
    Canceled: 'warning',
    Timeout: 'warning',
  };

  const voxStatusBodyTemplate = (rowData: VoxOrderDetail) => {
    return <Tag severity={orderStatusSeverityMap[rowData.status] || 'info'} value={rowData.status} className="text-xs" />;
  };

  const auwStatusBodyTemplate = (rowData: AuwOrderDetail) => {
    return <Tag severity={orderStatusSeverityMap[rowData.status] || 'info'} value={rowData.status} className="text-xs" />;
  };

  const totalCount =
    type === 'vox' ? (voxData?.totalCount ?? 0) :
    type === 'auw' ? (auwData?.totalCount ?? 0) :
    (apiData?.totalCount ?? 0);

  return (
    <Dialog
      header={getTitle()}
      visible={visible}
      onHide={onHide}
      style={{ width: '90vw', maxWidth: '1200px' }}
      maximizable
      modal
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <ProgressSpinner style={{ width: '50px', height: '50px' }} />
        </div>
      ) : type === 'vox' ? (
        <>
          <DataTable value={voxData?.items ?? []} size="small" stripedRows emptyMessage="No records found">
            <Column field="transactionName" header="Transaction" sortable />
            <Column field="integrationVendor" header="Vendor" sortable />
            <Column field="loanNumber" header="Loan #" sortable />
            <Column field="status" header="Status" body={voxStatusBodyTemplate} sortable />
            <Column field="itemCount" header="Items" sortable />
            <Column field="createdDate" header="Created" body={dateBodyTemplate} sortable />
            <Column field="providerTransactionId" header="External ID" />
          </DataTable>
          {totalCount > PAGE_SIZE && (
            <Paginator first={first} rows={PAGE_SIZE} totalRecords={totalCount} onPageChange={onPageChange} />
          )}
        </>
      ) : type === 'auw' ? (
        <>
          <DataTable value={auwData?.items ?? []} size="small" stripedRows emptyMessage="No records found">
            <Column field="transactionName" header="Transaction" sortable />
            <Column field="integrationVendor" header="Vendor" sortable />
            <Column field="loanNumber" header="Loan #" sortable />
            <Column field="status" header="Status" body={auwStatusBodyTemplate} sortable />
            <Column field="serviceRequestCount" header="Service Requests" sortable />
            <Column field="createdDate" header="Created" body={dateBodyTemplate} sortable />
            <Column field="providerTransactionId" header="External ID" />
          </DataTable>
          {totalCount > PAGE_SIZE && (
            <Paginator first={first} rows={PAGE_SIZE} totalRecords={totalCount} onPageChange={onPageChange} />
          )}
        </>
      ) : (
        <>
          <DataTable value={apiData?.items ?? []} size="small" stripedRows emptyMessage="No records found">
            <Column field="transactionName" header="Transaction" sortable />
            <Column field="method" header="Method" sortable />
            <Column field="success" header="Status" body={successBodyTemplate} sortable />
            <Column field="ruleExecutionMs" header="Rules" body={ruleExecutionBodyTemplate} sortable />
            <Column field="serviceProviderMs" header="3rd Party" body={serviceProviderBodyTemplate} sortable />
            <Column field="durationMs" header="Total" body={durationBodyTemplate} sortable />
            <Column field="requestTime" header="Request Time" body={dateBodyTemplate} sortable />
            <Column field="errorMessage" header="Error" style={{ maxWidth: '250px' }} />
          </DataTable>
          {totalCount > PAGE_SIZE && (
            <Paginator first={first} rows={PAGE_SIZE} totalRecords={totalCount} onPageChange={onPageChange} />
          )}
        </>
      )}
      <div className="text-sm text-gray-500 pt-2">
        Showing{' '}
        {type === 'vox'
          ? (voxData?.items?.length ?? 0)
          : type === 'auw'
            ? (auwData?.items?.length ?? 0)
            : (apiData?.items?.length ?? 0)}{' '}
        of {totalCount.toLocaleString()} records (Page {page})
      </div>
    </Dialog>
  );
};

export default DashboardDetailDialog;
